import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { PlatformModules } from "@miplatform/types";
import {
  useOrderTemplates,
  useWorkOrderDependencyMaps,
} from "../../../../hooks";
import {
  AutoTriggerFormValues,
  IExtendedWorkOrderDependencyMaps,
} from "./autoTrigger.types";
import {
  addDependencyToMap,
  buildUpsertRequest,
  deriveLabels,
  moveItemInMap,
  transformDependencyMaps,
  updateFieldInMap,
} from "./autoTrigger.utils";

// Re-export types so existing consumers don't break
export type {
  IExtendedWorkOrderDependencyMaps,
  SortedDependencyMap,
  Label,
} from "./autoTrigger.types";

export const useAutoTriggerModel = () => {
  const { getOrderTemplates, orderTemplates, setOrderTemplates } =
    useOrderTemplates({ module: PlatformModules.MiScheduling });

  const { dependencyMaps, getOrderTemplateWorkOrderTemplates } =
    useWorkOrderDependencyMaps();

  const stateManager = useComponentState();

  // ── Form ────────────────────────────────────────────────────────

  const form = useForm<AutoTriggerFormValues>({
    defaultValues: { sortedDependencyMap: {} },
  });

  const sortedDependencyMap = form.watch("sortedDependencyMap");
  const { isDirty } = form.formState;

  // Labels are fully derived from the dependency map — no separate state
  const labels = useMemo(
    () => deriveLabels(sortedDependencyMap),
    [sortedDependencyMap]
  );

  // Snapshot for link-edit cancel flow
  const linkEditSnapshotRef = useRef<AutoTriggerFormValues | null>(null);

  // ── Sync server data → form ─────────────────────────────────────

  useEffect(() => {
    form.reset({
      sortedDependencyMap: transformDependencyMaps(dependencyMaps),
    });
  }, [dependencyMaps]);

  // ── Fetching ──────────────────────────────────────────────────────

  const fetchOrderTemplates = async () => {
    const res = await getOrderTemplates();
    setOrderTemplates(res.status === HTTP_STATUSES.OK ? res.data : []);
  };

  const getOrderTemplateWorkOrder = async (id: string) => {
    await getOrderTemplateWorkOrderTemplates(id);
  };

  // ── Map mutations ─────────────────────────────────────────────────

  const moveItemToDayGroup = (
    currentDay: number,
    itemId: string,
    targetDay: number
  ) => {
    form.setValue(
      "sortedDependencyMap",
      moveItemInMap(sortedDependencyMap, currentDay, itemId, targetDay),
      { shouldDirty: true }
    );
  };

  const updateDependencyField = <
    K extends keyof IExtendedWorkOrderDependencyMaps
  >(
    day: number,
    itemId: string,
    field: K,
    value: IExtendedWorkOrderDependencyMaps[K]
  ) => {
    form.setValue(
      "sortedDependencyMap",
      updateFieldInMap(sortedDependencyMap, day, itemId, field, value),
      { shouldDirty: true }
    );
  };

  const addDependency = (
    principal: IExtendedWorkOrderDependencyMaps,
    dependent: IExtendedWorkOrderDependencyMaps,
    shouldAdd: boolean
  ) => {
    form.setValue(
      "sortedDependencyMap",
      addDependencyToMap(
        sortedDependencyMap,
        principal.id,
        principal.days,
        dependent.id,
        shouldAdd
      ),
      { shouldDirty: true }
    );
  };

  // ── Link-edit flow ────────────────────────────────────────────────

  const startLinkEdit = () => {
    linkEditSnapshotRef.current = form.getValues();
  };

  const cancelLinkEdit = () => {
    if (linkEditSnapshotRef.current) {
      form.reset(linkEditSnapshotRef.current, { keepDefaultValues: true });
      linkEditSnapshotRef.current = null;
    }
  };

  const confirmLinkEdit = () => {
    linkEditSnapshotRef.current = null;
  };

  // ── Save ──────────────────────────────────────────────────────────

  const save = (templateId: string) => {
    const requestMaps = buildUpsertRequest(sortedDependencyMap, dependencyMaps);

    stateManager.run(async () => {
      const response =
        await taskService.workOrderDependencyMaps.putOrderTemplateWorkOrderTemplates(
          {
            data: requestMaps,
            id: templateId,
            toastMessageConfig: {
              success: { text: "Dependencies updated successfully" },
            },
          }
        );

      if (response.status === HTTP_STATUSES.OK) {
        getOrderTemplateWorkOrderTemplates(templateId);
      }
    });
  };

  // ── Reset ─────────────────────────────────────────────────────────

  const reset = () => {
    form.reset({
      sortedDependencyMap: transformDependencyMaps(dependencyMaps),
    });
  };

  return {
    orderTemplates,
    fetchOrderTemplates,
    getOrderTemplateWorkOrder,
    sortedDependencyMap,
    labels,
    isDirty,
    addDependency,
    moveItemToDayGroup,
    updateDependencyField,
    startLinkEdit,
    cancelLinkEdit,
    confirmLinkEdit,
    isLoading: stateManager.isBusy(),
    save,
    reset,
  };
};
